import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store/create-store';
import { IAdminsState, IAdmins, IAdminsOBCheck } from 'store/admins/types';
import { getSelectedRecipient as getSelectedRecipientFromStorage } from 'services/storage';

export const getAdminsState = (state: RootState): IAdminsState => state.admins;

export const getAdminsData = createSelector([getAdminsState], (state: IAdminsState): IAdmins[] => {
  if (!state.data?.admin_users) return [];

  return state.data.admin_users.map(user => ({
    id: user.id || '',
    admin_name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    admin_role: user.role_type,
    admin_client_name: user.resource_name || '',
    admin_status: 'ACTIVE',
    resource_id: user.resource_id || '',
    resource_name: user.resource_name || '',
    first_name: user.first_name,
    last_name: user.last_name,
    role_type: user.role_type,
    role_names: user.role_names,
    resource_access: user.resource_access,
  }));
});

export const getAdminsOBCheck = createSelector(
  [getAdminsState],
  (state: IAdminsState): IAdminsOBCheck | null => state.OBCheck
);

export const getAdminsRecipients = createSelector(
  [getAdminsState],
  (state: IAdminsState) => state.recipients || []
);

export const getSelectedRecipientId = createSelector([getAdminsState], (state: IAdminsState) => {
  // First, check Redux state
  if (state.selectedRecipient?.id) {
    return state.selectedRecipient.id;
  }

  // Fall back to localStorage if Redux state is empty (e.g., on page refresh)
  const storedRecipient = getSelectedRecipientFromStorage();
  if (storedRecipient?.id) {
    // Verify the stored recipient exists in the current recipients list
    if (state.recipients && state.recipients.find(r => r.id === storedRecipient.id)) {
      return storedRecipient.id;
    }
  }

  // Final fallback to first recipient
  return state.recipients && state.recipients.length > 0 ? state.recipients[0].id : '';
});

export const getSelectedRecipient = createSelector([getAdminsState], (state: IAdminsState) => {
  // First, check Redux state
  if (state.selectedRecipient) {
    return state.selectedRecipient;
  }

  // Fall back to localStorage if Redux state is empty (e.g., on page refresh)
  const storedRecipient = getSelectedRecipientFromStorage();
  if (storedRecipient) {
    // Verify the stored recipient exists in the current recipients list and return the full object
    if (state.recipients) {
      const foundRecipient = state.recipients.find(r => r.id === storedRecipient.id);
      if (foundRecipient) {
        return foundRecipient;
      }
    }
  }

  // Final fallback to first recipient
  return state.recipients && state.recipients.length > 0 ? state.recipients[0] : null;
});

export const getAdminDetail = createSelector(
  [getAdminsState],
  (state: IAdminsState): IAdmins | null => state.adminDetail
);

export const getFilteredAdminsData = createSelector(
  [getAdminsData, (_state: any, filterStatus?: string) => filterStatus],
  (adminsData: IAdmins[], filterStatus?: string): IAdmins[] => {
    if (!adminsData) return [];

    if (!filterStatus || filterStatus === '') {
      return adminsData;
    }

    return adminsData.filter(admin => admin.admin_status === filterStatus);
  }
);

export const getActiveRole = createSelector(
  [getAdminsState],
  (state: IAdminsState) => state.activeRole
);

export const getAdminsPartners = createSelector(
  [getAdminsState],
  (state: IAdminsState) => state.partners || []
);
